import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * The two projects this admin can be pointed at.
 *
 * 2026-08-22 — `live` (SAC Pilot, dunktqzfprbjqxbcnooi) was removed and `stage`
 * (aqbuwpncrurkloczbvkg) put in its place. Reasons, recorded so this is not
 * re-litigated from memory:
 *
 *   1. `live` was never switched on. LIVE_ENV_ENABLED had been false since
 *      2026-04-16, so its nav section never rendered and no operator ever read
 *      that project through this app. Removing it costs nothing that was working.
 *   2. Stage is the promotion target (docs/rules/environments.md, GATE A —
 *      promotion by rotation: Stage becomes production in place, Proto retires).
 *      The admin has to be able to reach the project that becomes production
 *      BEFORE first signup, or the one-way door closes on an unexercised admin.
 *   3. The two envs answer two different needs. CORRECTED 2026-09-07 — this read
 *      "Proto still holds the curation working set and is where votes get processed
 *      end to end, while Stage is the quiet target being tested against." That
 *      inverted on 2026-08-25 when the curation agent was repointed: Stage is now
 *      where votes are processed end to end and where the editor works, and Proto is
 *      a read-only frozen snapshot the pilot app still reads. Both stay reachable,
 *      which is why this is still a switch and not a repoint — but the live one is
 *      Stage, and /stage/sourced-votes is where new drafts land.
 *
 * The stale comment this replaces claimed the Live URL var "currently points at
 * the Proto project". It did not — it pointed at SAC Pilot. Measured 2026-08-22.
 */
/*
 * 2026-09-28 — CS-5: the admin is Stage-only. Proto has been read-only since 2026-08-25 and the
 * admin's Proto side is retired (founder, 2026-09-28; security worklist §4 CS-5). `Env` stays a type
 * so the per-env plumbing (session key, project banner, ?env= on magic links) keeps its shape, but
 * it has one member. `/prototype/*` URLs redirect to `/stage/*` (App.tsx).
 */
//
// 2026-10-27 — dev-stage joins as a second tab (founder: "login once and toggle back and forth",
// as Proto/Stage worked before CS-5). `devstage` is mmhqfjryxbwhzlrtqmcs, test data only. Each env
// keeps its own saved session (supabase.ts storageKey), so after one sign-in to each, switching tabs
// needs no new login. Prod stays the default everywhere an env is not named.
export type Env = 'stage' | 'devstage';

export const ENVS: readonly Env[] = ['stage', 'devstage'] as const;

/** Human labels for the tab strip. The project NAME comes from getProjectInfo(). */
export const ENV_LABELS: Record<Env, string> = {
  // The env key stays 'stage' (routes /stage/*, storage keys); the project it points at has been
  // production since the 2026-10-16 launch, so that is what the admin calls it (founder, 2026-10-24).
  stage: 'Prod',
  devstage: 'Dev-stage',
};

const ENV_STORAGE_KEY = 'votd_admin_env';

export function isEnv(v: unknown): v is Env {
  return v === 'stage' || v === 'devstage';
}

/**
 * The env a path names, or null when the path carries no env at all.
 *
 * `/login` and `/auth/callback` are deliberately env-less: they exist before the
 * operator has chosen anything. Returning null rather than defaulting here is
 * what lets resolveEnv fall back to the remembered choice, so a magic-link
 * round-trip comes back to the env you left from instead of silently landing on
 * Proto.
 */
export function envFromPath(pathname: string): Env | null {
  if (pathname.startsWith('/stage'))     return 'stage';
  if (pathname.startsWith('/devstage'))  return 'devstage';
  // A retired /prototype/* URL is on its way to /stage/* (App.tsx redirect); it names Stage now.
  if (pathname.startsWith('/prototype')) return 'stage';
  return null;
}

/**
 * The env named by an `?env=` query parameter, or null.
 *
 * WHY THIS EXISTS — a real failure, 2026-08-22
 * --------------------------------------------
 * A magic link minted by Stage was opened and the callback tried to verify it
 * with the PROTO client, producing:
 *
 *   invalid JWT: unable to parse or verify signature, token is unverifiable:
 *   error while executing keyfunc: unrecognized JWT kid <uuid> for algorithm ES256
 *
 * which is Supabase saying "this token was signed by a key I do not have" —
 * i.e. by the other project. `/auth/callback` carries no env in its path, so it
 * fell back to the REMEMBERED env in localStorage. Email links open in whatever
 * the OS considers the default browser, and that browser profile had never
 * visited the app, so localStorage was empty and the fallback chose Proto.
 *
 * Browser storage cannot be part of this decision: the round-trip leaves the
 * browser entirely and may not come back to the same one. The env has to travel
 * WITH the link, so Login.tsx stamps `?env=<env>` onto emailRedirectTo and this
 * reads it back. A query parameter survives the mail client; localStorage does not.
 */
export function envFromSearch(search: string): Env | null {
  try {
    const v = new URLSearchParams(search).get('env');
    return isEnv(v) ? v : null;
  } catch {
    return null;
  }
}

export function rememberEnv(env: Env): void {
  try { window.localStorage.setItem(ENV_STORAGE_KEY, env); } catch { /* storage off */ }
}

/** Drop a remembered choice this build cannot honour. See resolveAuthEnv(). */
export function forgetEnv(): void {
  try { window.localStorage.removeItem(ENV_STORAGE_KEY); } catch { /* storage off */ }
}

export function recalledEnv(): Env {
  try {
    const v = window.localStorage.getItem(ENV_STORAGE_KEY);
    return isEnv(v) ? v : 'stage';
  } catch {
    return 'stage';
  }
}

/**
 * Path first, then an explicit `?env=` in the URL, then the remembered choice,
 * then Stage. Never throws.
 *
 * Order matters: an env-prefixed route is the strongest statement, `?env=` is an
 * explicit instruction carried across a browser boundary, and the remembered
 * value is only a convenience — it must never outrank something the URL says.
 */
export function resolveEnv(pathname: string, search = ''): Env {
  return envFromPath(pathname) ?? envFromSearch(search) ?? recalledEnv();
}

const EnvContext = createContext<Env>('stage');

export function EnvProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const env = resolveEnv(location.pathname);

  // Persist only when the PATH named the env. Writing on every resolve would
  // let a fallback overwrite the real choice with itself, which is harmless but
  // means the stored value stops being evidence of anything the operator did.
  useEffect(() => {
    if (envFromPath(location.pathname)) rememberEnv(env);
  }, [location.pathname, env]);

  return <EnvContext.Provider value={env}>{children}</EnvContext.Provider>;
}

export function useEnv() {
  return useContext(EnvContext);
}
